/** @jsx jsx */
import { jsx } from '@emotion/react';
// eslint-disable-next-line no-unused-vars
import React from 'react';
import { ORIENTATION_VALUES, SORT_VALUES, AGGREGATION_METHOD_VALUES } from '../chartUtils';
import { PropertyListSelector } from 'components/selectors/propertyListSelector';
import { EndpointParameterSelector } from 'components/selectors/endpointParameterSelector';
import { WidgetParameterSelector } from 'components/selectors/widgetParameterSelector';
import { ColorSelector } from 'components/selectors/colorSelector';
import { LimitSelector } from 'components/selectors/limitSelector';
import { EndpointPropertySelector } from 'components/selectors/endpointPropertySelector';
import { PropertyType as SrsPropertyType, PropertyFlag as SrsPropertyFlag } from 'srs-middleware';
import { WidgetBooleanSelector } from 'components/selectors/widgetBooleanSelector';
import { EndpointSortListSelector } from 'components/selectors/endpointSortListSelector';
import CommonKnobsContainer from 'views/common/commonKnobsContainer';
import { Tab, Tabs } from '@blueprintjs/core';
import ColorByValuesSelector from 'components/selectors/colorByValuesSelector';

/**
 * Custom renderer for the controls available to change the chart settings.
 *
 * Data Properties for this Widget:
 *   -- propertyList
 *   -- limit
 *
 * Chart Properties for this Widget:
 * @param cardIndex
 * @param endpointQuery
 * @param widgetConfiguration
 * @returns {*}
 * @constructor
 */
export const StackedBarGraphKnobs = ({ cardIndex, endpointQuery, widgetConfiguration }) => {
	return (
		<Tabs>
			<Tab
				id="data"
				title="Data"
				panel={
					<React.Fragment>
						<CommonKnobsContainer>
							<PropertyListSelector
								cardIndex={cardIndex}
								itemIndex={0}
								flags={[SrsPropertyFlag.Categorical]}
								label={'Bin By'}
								endpointQuery={endpointQuery}
								cy-test="bin-by-dropdown-button"
							/>
							<PropertyListSelector
								cardIndex={cardIndex}
								itemIndex={1}
								flags={[SrsPropertyFlag.Categorical]}
								label={'Group By'}
								endpointQuery={endpointQuery}
								cy-test="group-by-dropdown-button"
								isNullable={true}
							/>
							<EndpointSortListSelector
								cardIndex={cardIndex}
								endpointQuery={endpointQuery}
								propertyName="sortList"
								label="Bin Sort By"
								sortIndex={0}
								cy-test="bin-sort-by-dropdown-button"
							/>
							<EndpointSortListSelector
								cardIndex={cardIndex}
								endpointQuery={endpointQuery}
								propertyName="sortList"
								label="Group Sort By"
								sortIndex={1}
								cy-test="group-sort-by-dropdown-button"
							/>
						</CommonKnobsContainer>
						<CommonKnobsContainer>
							<EndpointPropertySelector
								cardIndex={cardIndex}
								endpointQuery={endpointQuery}
								propertyName="aggregationProperty"
								types={[SrsPropertyType.Numeric]}
								label="Aggregation Property"
								isNullable={true}
								cy-test="aggregation-dropdown-button"
							/>
							<EndpointParameterSelector
								cardIndex={cardIndex}
								endpointQuery={endpointQuery}
								propertyName="aggregationMethod"
								label="Aggregation Method"
								values={AGGREGATION_METHOD_VALUES}
								defaultValue={AGGREGATION_METHOD_VALUES[0]}
								cy-test="aggregation-method-dropdown-button"
							/>
							<EndpointPropertySelector
								cardIndex={cardIndex}
								endpointQuery={endpointQuery}
								propertyName="distinctProperty"
								flags={[SrsPropertyFlag.Categorical]}
								types={[SrsPropertyType.String]}
								label="Distinct Property"
								isNullable={true}
								cy-test="distinct-dropdown-button"
							/>
						</CommonKnobsContainer>
						<CommonKnobsContainer>
							<LimitSelector
								cardIndex={cardIndex}
								endpointQuery={endpointQuery}
								initialLimit={20}
								label={'Bar Count'}
								limitProperty="maxRoot"
								cy-test="bar-count-dropdown-button"
							/>
							<LimitSelector
								cardIndex={cardIndex}
								endpointQuery={endpointQuery}
								initialLimit={1000}
								label={'Child Count'}
								limitProperty="maxNodes"
								cy-test="child-count-dropdown-button"
							/>
						</CommonKnobsContainer>
					</React.Fragment>
				}
			/>
			<Tab
				id="style"
				title="Style"
				panel={
					<React.Fragment>
						<CommonKnobsContainer>
							<ColorSelector
								cardIndex={cardIndex}
								widgetConfiguration={widgetConfiguration}
								activeItem={widgetConfiguration?.colorScale}
								cy-test="color-using-dropdown-button"
							/>
							<ColorByValuesSelector
								type={"STACKED_BAR_GRAPH"}
								cardIndex={cardIndex}
								widgetConfiguration={widgetConfiguration}
								propertyName={'colorByValue'}
								label={'Color Based on Values (alpha)'}
								cy-test="color-by-values-checkbox"
							/>
							<WidgetParameterSelector
								cardIndex={cardIndex}
								widgetConfiguration={widgetConfiguration}
								propertyName="direction"
								label="Orientation"
								values={ORIENTATION_VALUES}
								defaultValue={ORIENTATION_VALUES[0]}
								cy-test="orientation-dropdown-button"
							/>
						</CommonKnobsContainer>
						<CommonKnobsContainer>
							<WidgetBooleanSelector
								cardIndex={cardIndex}
								widgetConfiguration={widgetConfiguration}
								propertyName={'showBarLabels'}
								label={'Show Bar Labels'}
								value={false}
							/>
							<WidgetBooleanSelector
								cardIndex={cardIndex}
								widgetConfiguration={widgetConfiguration}
								propertyName={'limitAxisLabels'}
								label={'Limit Axis Labels'}
								value={true}
							/>
						</CommonKnobsContainer>
					</React.Fragment>
				}
			/>
		</Tabs>
	);
};
